import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { Add as AddIcon, MenuBook as LensIcon } from "@mui/icons-material";
import { useAppDispatch } from "../../store/hooks";
import { setSnackAction } from "../../store/slices/snackSlice";
import lensService, { type GenerateRequest } from "../../services/lensService";
import usePlaybooks from "./hooks/usePlaybooks";
import useLensMutation from "./hooks/useLensMutation";
import useStartGeneration from "./hooks/useStartGeneration";
import PlaybookTable from "./PlaybookTable";
import PlaybookGenerator from "./PlaybookGenerator";

const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(3) },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(4),
    },
    headerTitle: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
}));

const Playbooks = () => {
    const { classes } = useStyles();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const playbooks = usePlaybooks();
    const createPlaybook = useLensMutation(lensService.createPlaybook);
    const updatePlaybook = useLensMutation(lensService.updatePlaybook);
    const generation = useStartGeneration();
    const [generatorOpen, setGeneratorOpen] = useState(false);

    const open = (key: string) => navigate(`/lens/playbooks/${encodeURIComponent(key)}`);

    // The playbook is created as soon as the request is checked; its page then follows the generation.
    const generate = (request: GenerateRequest) =>
        generation.start({
            key: request.key,
            request: () => lensService.generatePlaybook(request),
            started: () => true,
            failMessage: "Could not generate this playbook.",
            onStarted: (playbook) => open(playbook.key),
        });

    const createEmpty = (key: string, displayName: string) =>
        createPlaybook.mutate(
            { key, displayName },
            {
                onSuccess: (created) => {
                    dispatch(setSnackAction(`Created "${created.displayName}"`, "success"));
                    open(created.key);
                },
            },
        );

    const restore = (key: string) =>
        updatePlaybook.mutate(
            { key, changes: { archived: false } },
            { onSuccess: (restored) => dispatch(setSnackAction(`Restored "${restored.displayName}"`, "success")) },
        );

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Box className={classes.headerTitle}>
                    <LensIcon style={{ color: "#7a5cf0", fontSize: 32 }} />
                    <Typography variant="h4">Lens Playbooks</Typography>
                </Box>
                {!generatorOpen && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setGeneratorOpen(true)}>
                        New playbook
                    </Button>
                )}
            </Box>

            {generatorOpen && (
                <PlaybookGenerator
                    existing={playbooks.data ?? []}
                    starting={generation.starting}
                    isCreating={createPlaybook.isPending}
                    onGenerate={generate}
                    onCreateEmpty={createEmpty}
                    onCancel={() => setGeneratorOpen(false)}
                />
            )}

            <PlaybookTable
                playbooks={playbooks.data ?? []}
                isLoading={playbooks.isLoading}
                isError={playbooks.isError}
                onOpen={open}
                onRestore={restore}
                restoringKey={updatePlaybook.isPending ? (updatePlaybook.variables?.key ?? null) : null}
            />
        </Box>
    );
};

export default Playbooks;
